import { Location } from '@angular/common';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { HashStorageService } from '@df/browser/storage/hash-storage.service';
import { Category, ICategoryDataV2 } from '@df/catalog/category/category';
import { App } from '@df/core/app';
import { Meta } from '@df/core/meta/meta';
import { StandardSerializer } from '@df/util/fn/standard-serializer';
import sortBy from 'lodash-es/sortBy';
import take from 'lodash-es/take';
import { NFacetUrl } from './facet.interfaces';

@Injectable({
    providedIn: 'root'
})
export class FacetUrlService {
    protected useHardNavigation = false;

    /**
     * Dependencies
     */
    constructor(
        private meta: Meta,
        private app: App,
        private hashStorageService: HashStorageService,
        private router: Router,
        private location: Location
    ) {}

    /**
     * Retrieves facet url module configuration
     */
    private getConfig() {
        return this.app.data.facet;
    }

    /**
     * Retrieves ordered list of attributes used for facet URLs
     */
    getFacetAttributes(category: Category<ICategoryDataV2>, data: { [attrCode: string]: string | number | string[] | number[] }): string[] {
        return this.getConfig()?.pattern || [];
    }

    /**
     * Picks nax number of components we can have in facet url
     */
    getFacetComponentsLimit(): number | undefined {
        const maxDepth = this.getConfig()?.maxDepth || -1;

        if (maxDepth >= 0) {
            return maxDepth;
        }

        return undefined;
    }

    getFacetIndexLimit(): number | undefined {
        const maxDepthIndex = this.getConfig()?.maxDepthIndex || -1;

        if (maxDepthIndex >= 0) {
            return maxDepthIndex;
        }

        return undefined;
    }

    /**
     * Retrieves facet url data from meta if category matches
     */
    retrieveFacetData(categoryId: number): NFacetUrl.IFacetData | undefined {
        const facetData = this.meta.currentRoute?.data?.facet;

        if (facetData?.category === categoryId) {
            return facetData;
        }

        return undefined;
    }

    createFacetUrl(category: Category<ICategoryDataV2>, data: { [attributeCode: string]: string | number | string[] | number[] }): string {
        const parts = this.getFacetUrlParts(category, data);
        const path = parts.join('/');

        const hash = this.hashStorageService.getHash();
        const newPath = [category.data!.url, path].filter(i => !!i).join('/');
        return newPath + (hash ? '#' + hash : '');
    }

    getFacetUrlParts(
        category: Category<ICategoryDataV2>,
        data: { [attributeCode: string]: string | number | string[] | number[] }
    ): (string | number)[] {
        let parts = sortBy(this.getFacetAttributes(category, data), attrCode => {
            return this.app.resources.attribute.getExisting(attrCode)?.data?.position || 0;
        })
            .map(i => data[i])
            .filter(i => !!i)
            .map(i => StandardSerializer.serialize(i.toString().replace(/\s+/g, '-').toLowerCase()))
            .filter(i => !!i);

        if (this.getFacetComponentsLimit()) {
            parts = take(parts, this.getFacetComponentsLimit());
        }

        return parts;
    }

    setFacetUrl(category: Category<ICategoryDataV2>, data: { [attributeCode: string]: string | number }) {
        const newUrl = this.createFacetUrl(category, data);
        const newPath = newUrl.split('#')[0];

        if (this.useHardNavigation) {
            this.hardNavigationChange(newPath);
        } else {
            this.softNavigationChange(newUrl, newPath);
        }
    }

    private hardNavigationChange(newPath: string) {
        this.router.navigateByUrl(newPath, { skipLocationChange: true });

        this.location.replaceState(newPath);
    }

    private softNavigationChange(newUrl: string, newPath: string) {
        this.location.replaceState(newUrl);

        const route = this.app.resources.route.getCreate(newPath);
        route.load(1).then(() => {
            if (this.location.path() === newPath) {
                this.meta.updateRoute(route);
            }
        });
    }

    /**
     * Checks if category supports facet urls
     */
    usesFacetUrls(category: Category<ICategoryDataV2>) {
        return !!category.data!.facets;
    }
}
